import {
  FunnelBar,
  FunnelBarVm,
  PipelineCards,
  PipelineCardTier,
  PipelineCardVm,
  PipelineFunnelStage,
  PipelineRatio,
} from '@partner/feature-modules/dashboard/domain/model/pipeline-health.model';

/** Rendered for a percentage that has no denominator — never as "0%". */
export const NO_VALUE_LABEL = '—';

/**
 * Smallest track a bar with rows may draw, as a percentage of the funnel width. The count sits inside the
 * bar, so a narrower one has nowhere to put it (Funded is ~0.2% of Started on real data). Floored bars are
 * not proportional.
 */
export const MIN_BAR_WIDTH_PERCENT = 8;

export const PIPELINE_STAGE_LABELS: Record<PipelineFunnelStage, string> = {
  [PipelineFunnelStage.STARTED]: 'Started',
  [PipelineFunnelStage.SUBMITTED]: 'Submitted',
  [PipelineFunnelStage.PROCESSING]: 'Processing (In review)',
  [PipelineFunnelStage.APPROVED]: 'Approved',
  [PipelineFunnelStage.CLOSING]: 'Closing / Contract',
  [PipelineFunnelStage.OPEN]: 'Open',
  [PipelineFunnelStage.FUNDED]: 'Funded',
};

export const PIPELINE_CARD_TITLES: Record<keyof PipelineCards, string> = {
  blockedOnIncomplete: 'Pipeline blocked on incomplete',
  approvedVsSubmitted: 'Approved vs Submitted',
  fundedVsApproved: 'Funded vs Approved',
};

/**
 * Unit named on the `n of N` line, and only where the denominator is a count of deals. Sits beside the
 * titles so a card's copy cannot be assembled from two halves that disagree.
 */
export const PIPELINE_CARD_UNITS: Record<keyof PipelineCards, string | null> = {
  blockedOnIncomplete: 'deals',
  approvedVsSubmitted: null,
  fundedVsApproved: null,
};

/** Shown for a rate above zero that would print as 0.0%. */
export const BELOW_ONE_TENTH_LABEL = '<0.1%';

/** Shown for a rate below 100 that would print as 100.0%. */
export const ABOVE_NINETY_NINE_POINT_NINE_LABEL = '>99.9%';

/**
 * One label rule for all seven bars and all three cards: whole percent, and every exact edge prints whole. A
 * value off an edge that would round onto it prints one decimal; if the decimal would land on the edge too, it
 * prints `<0.1%` or `>99.9%` at the totals and steps one tenth off any other edge. `edges` is the caller's:
 * `CARD_LABEL_EDGES` or `BAR_LABEL_EDGES`. The server already rounded to four places.
 */
export function percentLabel(percent: number | null, edges: readonly number[]): string {
  if (percent === null) {
    return NO_VALUE_LABEL;
  }
  const whole = Math.round(percent);
  if (!edges.includes(whole) || percent === whole) {
    return `${whole}%`;
  }
  const tenth = Math.round(percent * 10) / 10;
  if (tenth !== whole) {
    return `${tenth.toFixed(1)}%`;
  }
  if (whole === 0 && percent > 0) {
    return BELOW_ONE_TENTH_LABEL;
  }
  if (whole === 100 && percent < 100) {
    return ABOVE_NINETY_NINE_POINT_NINE_LABEL;
  }
  return `${(percent > whole ? whole + 0.1 : whole - 0.1).toFixed(1)}%`;
}

/**
 * Width of the bar's track. Null means the cohort is empty and zero means the bar is empty, and both draw
 * nothing — the floor applies only once the bar has rows, so an empty bar never renders a filled track.
 */
export function barWidthPercent(percentOfStarted: number | null, count: number): number {
  if (percentOfStarted === null || count <= 0) {
    return 0;
  }
  return Math.max(percentOfStarted, MIN_BAR_WIDTH_PERCENT);
}

/** Null on every bar that carries no badge, and on the badged bar when nothing is currently aging. */
export function agingChipLabel(agingCount: number | null, thresholdDays: number): string | null {
  if (agingCount === null || agingCount <= 0) {
    return null;
  }
  return `${agingCount} aging > ${thresholdDays}d`;
}

export function agingLegendLabel(thresholdDays: number): string {
  return `Aging > ${thresholdDays} days`;
}

/** `unit` is required, not defaulted: a card that names no unit has to say so rather than inherit one. */
export function countLabel(numerator: number, denominator: number, unit: string | null): string {
  const pair = `${numerator} of ${denominator}`;
  return unit === null ? pair : `${pair} ${unit}`;
}

export function toFunnelBarVm(bar: FunnelBar, agingThresholdDays: number): FunnelBarVm {
  return {
    stage: bar.stage,
    label: PIPELINE_STAGE_LABELS[bar.stage],
    count: bar.count,
    percentLabel: percentLabel(bar.percentOfStarted, BAR_LABEL_EDGES),
    widthPercent: barWidthPercent(bar.percentOfStarted, bar.count),
    agingLabel: agingChipLabel(bar.agingCount, agingThresholdDays),
  };
}

/**
 * Traffic-light bands for the dashboard cards; the rule table is in `.claude/knowledge/dashboard-metrics.md`.
 * Both edges are closed, so the edge value is yellow. `benchmarkTier` is implemented and specced but not yet
 * wired up — no endpoint supplies a peer median — so do not delete it as dead code.
 */
export const ABSOLUTE_BAND_EDGE_PERCENT = 20;
export const BENCHMARK_GOOD_RATIO = 1;
export const BENCHMARK_WARNING_RATIO = 0.75;

/** The peer median `benchmarkTier` is waiting on; nothing on the wire carries one yet. */
export const PEER_MEDIAN_UNAVAILABLE: number | null = null;

/** The totals: a rate off none or all must never print as either. */
const TOTAL_LABEL_EDGES: readonly number[] = [0, 100];

/** Bars carry no colour, so only the totals. */
export const BAR_LABEL_EDGES: readonly number[] = TOTAL_LABEL_EDGES;

/**
 * The totals plus the absolute band edges (0 and 20), so a card's label never reads as a band its colour does not
 * state. Must change when `benchmarkTier` takes over the two conversion cards.
 */
export const CARD_LABEL_EDGES: readonly number[] = [...TOTAL_LABEL_EDGES, ABSOLUTE_BAND_EDGE_PERCENT];

/** Zero-is-best rate: green only at exactly 0, yellow above 0 through the edge, red beyond it. */
export function zeroIsBestTier(percent: number | null): PipelineCardTier {
  if (percent === null) {
    return PipelineCardTier.NO_VALUE;
  }
  if (percent <= 0) {
    return PipelineCardTier.GOOD;
  }
  return percent <= ABSOLUTE_BAND_EDGE_PERCENT ? PipelineCardTier.WARNING : PipelineCardTier.DANGER;
}

/** Higher-is-better rate on the same edge, read the other way: red only at exactly 0, green past the edge. */
export function higherIsBetterTier(percent: number | null): PipelineCardTier {
  if (percent === null) {
    return PipelineCardTier.NO_VALUE;
  }
  if (percent <= 0) {
    return PipelineCardTier.DANGER;
  }
  return percent <= ABSOLUTE_BAND_EDGE_PERCENT ? PipelineCardTier.WARNING : PipelineCardTier.GOOD;
}

/** ±10% on a change against the preceding period: the colour table's trend/delta rows. */
export const TREND_BAND_EDGE_PERCENT = 10;

export enum TrendDirection {
  HIGHER_IS_BETTER = 'higher-is-better',
  LOWER_IS_BETTER = 'lower-is-better',
}

/** Both edges closed and green or red; strictly between is yellow, a real 0 included. Inverted for lower-is-better. */
export function trendTier(changePercent: number | null, direction: TrendDirection): PipelineCardTier {
  if (changePercent === null) {
    return PipelineCardTier.NO_VALUE;
  }
  const improvement = direction === TrendDirection.LOWER_IS_BETTER ? -changePercent : changePercent;
  if (improvement >= TREND_BAND_EDGE_PERCENT) {
    return PipelineCardTier.GOOD;
  }
  return improvement <= -TREND_BAND_EDGE_PERCENT ? PipelineCardTier.DANGER : PipelineCardTier.WARNING;
}

/**
 * Higher-is-better rate judged against a peer median. Without a median there is nothing to judge, so it
 * answers `UNBENCHMARKED` rather than inventing a threshold.
 */
export function benchmarkTier(percent: number | null, peerMedianPercent: number | null): PipelineCardTier {
  if (percent === null) {
    return PipelineCardTier.NO_VALUE;
  }
  if (peerMedianPercent === null || peerMedianPercent <= 0) {
    return PipelineCardTier.UNBENCHMARKED;
  }
  const ratio = percent / peerMedianPercent;
  if (ratio >= BENCHMARK_GOOD_RATIO) {
    return PipelineCardTier.GOOD;
  }
  return ratio >= BENCHMARK_WARNING_RATIO ? PipelineCardTier.WARNING : PipelineCardTier.DANGER;
}

/** Keyed by the card rather than handed a title, so the title and the unit always come from one entry. */
export function toPipelineCardVm(
  card: keyof PipelineCards,
  ratio: PipelineRatio,
  tier: PipelineCardTier,
): PipelineCardVm {
  return {
    title: PIPELINE_CARD_TITLES[card],
    percentLabel: percentLabel(ratio.percent, CARD_LABEL_EDGES),
    countLabel: countLabel(ratio.numerator, ratio.denominator, PIPELINE_CARD_UNITS[card]),
    progressPercent: ratio.percent ?? 0,
    tier,
  };
}
